import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ClinicalHero from './components/ClinicalHero';
import ServicesGrid from './components/ServicesGrid';
import ClinicalFAQ from './components/ClinicalFAQ';
import InsuranceInfo from './components/InsuranceInfo';
import { setPageMeta, injectJsonLd, removeJsonLd } from '../../utils/seo';

const ClinicalPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    
    // Set page meta tags
    setPageMeta(
      'Clinical Acupuncture Services | Vionne Stability - Insurance Accepted',
      'Professional clinical acupuncture services in Vancouver. Insurance accepted. Specialized treatments for pain management, stress relief, hormonal balance, and digestive health.',
      `${siteUrl}/clinical-services`
    );

    // Inject JSON-LD schema
    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'MedicalBusiness',
        '@id': `${siteUrl}/clinical-services#medical`,
        name: 'Vionne Stability Clinical Acupuncture',
        url: `${siteUrl}/clinical-services`,
        image: 'https://public.readdy.ai/ai/img_res/2733040e4cad0a5b143155bcf6f332ab.jpg',
        description: 'Professional clinical acupuncture services with insurance coverage for pain management, stress relief, hormonal balance, and holistic wellness',
        priceRange: '$$',
        paymentAccepted: ['Insurance', 'Cash', 'Credit Card', 'Debit Card'],
        currenciesAccepted: 'CAD',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Vancouver',
          addressRegion: 'BC',
          addressCountry: 'CA'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 49.2827,
          longitude: -123.1207
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00'
          }
        ],
        medicalSpecialty: 'Acupuncture',
        availableService: [
          {
            '@type': 'MedicalTherapy',
            name: 'Pain Management Acupuncture',
            description: 'Specialized acupuncture treatment for chronic and acute pain relief'
          },
          {
            '@type': 'MedicalTherapy',
            name: 'Stress & Anxiety Relief',
            description: 'Acupuncture therapy for nervous system regulation and stress management'
          },
          {
            '@type': 'MedicalTherapy',
            name: 'Hormonal Balance',
            description: 'Acupuncture treatment for hormonal regulation and reproductive health'
          },
          {
            '@type': 'MedicalTherapy',
            name: 'Digestive Health',
            description: 'Acupuncture therapy for digestive system optimization'
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${siteUrl}/clinical-services#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Is acupuncture covered by insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, many extended health insurance plans in BC cover acupuncture services. We provide receipts for insurance claims.'
            }
          },
          {
            '@type': 'Question',
            name: 'How many sessions will I need?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Treatment plans vary based on individual needs. Most clients see improvement within 4-6 sessions, with ongoing maintenance recommended.'
            }
          },
          {
            '@type': 'Question',
            name: 'Does acupuncture hurt?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most people feel minimal to no discomfort. Acupuncture needles are very thin, and the sensation is often described as a mild tingling or pressure.'
            }
          }
        ]
      }
    ];

    injectJsonLd(schema);

    return () => {
      removeJsonLd();
    };
  }, []);

  return (
    <div className="min-h-screen bg-warmWhite">
      <Navbar />
      <main>
        <ClinicalHero />
        <ServicesGrid />
        <ClinicalFAQ />
        <InsuranceInfo />
      </main>
      <Footer />
    </div>
  );
};

export default ClinicalPage;